import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { PointsBadge } from '@/components/PointsBadge'
import { LoadingScreen } from '@/components/LoadingScreen'
import { getLocale, toIntlLocale } from '@/config/locale'
import type {
  HomeBootstrap,
  HomeRecentMatches,
  UserBadge,
} from '@/features/home/api'
import {
  fetchDashboardBadgesEarnedToday,
  usePouleHomeSummary,
  useRecentMatches,
} from '@/features/home/api'
import type {
  LiveMatchesResponse,
  NextMatchesResponse,
} from '@/features/match-center/api'
import {
  useLiveMatches,
  useNextMatches,
  useSavePrediction,
} from '@/features/match-center/api'
import { getGreetingName, useCurrentUser } from '@/features/user/api'
import {
  formatKickoffDateTime,
  getKickoffSource,
  getKickoffTimeValue,
} from '@/lib/kickoff'
import {
  type PublicPouleSearchItem,
  usePublicPoules,
} from '@/features/poules/api'
import {
  type JoinedPoule,
  type MyStatsDashboard,
  useJoinedPoules,
  useMyStatsDashboard,
} from '@/features/my-golero/api'
import { translate, useTranslation } from '@/i18n'
import { PouleCreatePage } from '@/pages/PouleCreatePage'

type DashboardView = 'overview' | 'poules' | 'stats'
type MatchCenterView = 'predictions' | 'live'

interface DisplayMatch {
  id: number
  homeName: string
  awayName: string
  homeLogo: string | undefined
  awayLogo: string | undefined
  kickoff: string | undefined
  homePred: number | undefined
  awayPred: number | undefined
  representativePouleId: number | undefined
  isPredictable: boolean
  canPredict: boolean
  isCancelled: boolean
  statusLabel: string
  scopeText: string | undefined
  groupLabel: string | undefined
}

interface LiveDisplayMatch {
  id: number
  representativePouleId: number | undefined
  pouleMatchId: number | undefined
  homeName: string
  awayName: string
  homeLogo: string | undefined
  awayLogo: string | undefined
  kickoff: string | undefined
  minuteLabel: string
  statusLabel: string
  homeScore: number | undefined
  awayScore: number | undefined
  virtualPoints: number | undefined
}

interface RecentDisplayMatch {
  id: number
  homeName: string
  awayName: string
  homeLogo: string | undefined
  awayLogo: string | undefined
  kickoff: string | undefined
  homeScore: number | undefined
  awayScore: number | undefined
  homePred: number | undefined
  awayPred: number | undefined
  pointsAwarded: number
}

interface HomeBadgeMatch {
  id: number
  homeName: string
  awayName: string
  homeLogo: string | undefined
  awayLogo: string | undefined
  kickoff: string | undefined
  homeScore: number | undefined
  awayScore: number | undefined
  homePred: number | undefined
  awayPred: number | undefined
  pointsAwarded: number | undefined
}

type HomeNextSummary = NonNullable<HomeBootstrap['next_matches_summary']>
type HomeLiveSummary = NonNullable<HomeBootstrap['live_matches_summary']>
type HomeDashboard = NonNullable<HomeBootstrap['dashboard']>
type HomeBadge = UserBadge
type HomeDashboardPoule = HomeDashboard['poules'][number]
type HomeDashboardMatch = HomeDashboardPoule['matches'][number]
type RecentMatchesPoule = HomeRecentMatches['poules'][number]
type RecentMatch = RecentMatchesPoule['matches'][number]
type MatchesData = NextMatchesResponse
type LiveMatchesData = LiveMatchesResponse
type MatchesTab = MatchesData['tabs'][number]
type DedupItem = NonNullable<MatchesData['deduplicated_matches']>['items'][number]
type PouleSection = MatchesData['poules'][number]
type PouleMatch = PouleSection['matches'][number]
type LivePouleSection = LiveMatchesData['poules'][number]
type LivePouleMatch = LivePouleSection['matches'][number]

const QUICK_SCORE_COLUMNS: Array<{
  key: 'home' | 'draw' | 'away'
  align?: 'center'
  scores: Array<[number, number]>
}> = [
  {
    key: 'home',
    scores: [
      [1, 0],
      [2, 0],
      [2, 1],
      [3, 0],
      [3, 1],
    ],
  },
  {
    key: 'draw',
    align: 'center',
    scores: [
      [0, 0],
      [1, 1],
      [2, 2],
    ],
  },
  {
    key: 'away',
    scores: [
      [0, 1],
      [0, 2],
      [1, 2],
      [0, 3],
      [1, 3],
    ],
  },
]

export function DashboardPage() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const locale = getLocale()
  const currentUserQuery = useCurrentUser(Boolean(user))
  const {
    data: bootstrap,
    isLoading,
    isError,
    refetch: refetchBootstrap,
    isFetching: isFetchingBootstrap,
  } = usePouleHomeSummary(locale)
  const [activeView, setActiveView] = useState<DashboardView>('overview')
  const joinedPoulesQuery = useJoinedPoules(1, activeView === 'poules')
  const myStatsQuery = useMyStatsDashboard(locale, activeView === 'stats')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [matchCenterView, setMatchCenterView] = useState<MatchCenterView>('predictions')
  const [isBadgesOpen, setIsBadgesOpen] = useState(false)
  const [lazyBadges, setLazyBadges] = useState<HomeBadge[] | null>(null)
  const [isLoadingLazyBadges, setIsLoadingLazyBadges] = useState(false)
  const [lazyBadgesError, setLazyBadgesError] = useState('')
  const [isRecentPointsOpen, setIsRecentPointsOpen] = useState(false)
  const [isPouleCreateOpen, setIsPouleCreateOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined)
  const [selectedMatch, setSelectedMatch] = useState<DisplayMatch | null>(null)
  const [homePred, setHomePred] = useState<number | ''>('')
  const [awayPred, setAwayPred] = useState<number | ''>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedMatchCenterView: MatchCenterView | null =
    searchParams.get('open') === 'live'
      ? 'live'
      : searchParams.get('open') === 'predictions'
        ? 'predictions'
        : null
  const shouldOpenMatchCenter = requestedMatchCenterView !== null
  const recentMatchesQuery = useRecentMatches(locale, isRecentPointsOpen)
  const nextMatchesQuery = useNextMatches({
    locale,
    date: selectedDate,
    enabled: isDrawerOpen,
  })
  const liveMatchesQuery = useLiveMatches({
    locale,
    enabled: isDrawerOpen,
  })
  const savePrediction = useSavePrediction()

  const predictionQueue = useMemo(() => {
    const queue = getPredictionQueue(nextMatchesQuery.data)
    if (!selectedMatch) return queue

    const selectedKey = getDisplayMatchKey(selectedMatch)
    return queue.some((match) => getDisplayMatchKey(match) === selectedKey)
      ? queue
      : [selectedMatch, ...queue]
  }, [nextMatchesQuery.data, selectedMatch])

  const selectedPredictionIndex = selectedMatch
    ? predictionQueue.findIndex(
        (match) => getDisplayMatchKey(match) === getDisplayMatchKey(selectedMatch),
      )
    : -1

  useEffect(() => {
    const hasPoules = bootstrap?.joined_any?.has_poules
    if (
      !shouldOpenMatchCenter ||
      isLoading ||
      isError ||
      hasPoules === false
    ) {
      return
    }

    setMatchCenterView(requestedMatchCenterView)
    setIsDrawerOpen(true)
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        nextParams.delete('open')
        return nextParams
      },
      { replace: true },
    )
  }, [bootstrap, isError, isLoading, requestedMatchCenterView, setSearchParams, shouldOpenMatchCenter])

  useEffect(() => {
    if (!isDrawerOpen) return

    void refetchBootstrap()
  }, [isDrawerOpen, refetchBootstrap])

  useEffect(() => {
    if (!isDrawerOpen) {
      setSelectedMatch(null)
      setSelectedDate(undefined)
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isDrawerOpen])

  useEffect(() => {
    if (activeView === 'overview') return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [activeView])

  useEffect(() => {
    setHomePred(selectedMatch?.homePred ?? '')
    setAwayPred(selectedMatch?.awayPred ?? '')
  }, [selectedMatch])

  const submitPrediction = (nextHomePred: number, nextAwayPred: number) => {
    if (
      !selectedMatch ||
      selectedMatch.representativePouleId == null ||
      !selectedMatch.canPredict
    ) {
      return
    }

    savePrediction.mutate(
      {
        matchId: selectedMatch.id,
        pouleId: selectedMatch.representativePouleId,
        homePred: nextHomePred,
        awayPred: nextAwayPred,
      },
      {
        onSuccess: async () => {
          const nextMatch = getNextPredictionMatch(
            nextMatchesQuery.data,
            selectedMatch,
          )
          savePrediction.reset()
          setSelectedMatch(nextMatch)

          const [refreshedMatches] = await Promise.all([
            nextMatchesQuery.refetch(),
            refetchBootstrap(),
          ])

          if (!nextMatch) return

          const freshNextMatch = getAllDisplayMatches(refreshedMatches.data).find(
            (match) => getDisplayMatchKey(match) === getDisplayMatchKey(nextMatch),
          )
          if (freshNextMatch) setSelectedMatch(freshNextMatch)
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingScreen compact />
        <DashboardPageSkeleton />
      </div>
    )
  }

  if (isError || !bootstrap) {
    return (
      <ErrorPanel
        title={t('Home niet geladen')}
        body={t('Controleer de backend, je Firebase-sessie en probeer opnieuw.')}
        cta={t('Opnieuw proberen')}
        onClick={() => void refetchBootstrap()}
      />
    )
  }

  const hasPoules = bootstrap.joined_any?.has_poules
  const liveSummary = bootstrap.live_matches_summary
  const nextSummary = bootstrap.next_matches_summary
  const dashboard = bootstrap.dashboard
  const embeddedBadges = getDashboardBadges(dashboard)
  const badgesCount = getDashboardBadgesCount(dashboard)
  const userLabel = currentUserQuery.data
    ? getGreetingName(currentUserQuery.data)
    : getUserLabel(user?.displayName, t('Speler'))
  const matchCenterState = getMatchCenterState(liveSummary, nextSummary, locale, t)
  const hasLiveMatches = getHasLiveMatches(liveSummary)
  const hasPredictionMatches = Boolean(nextSummary?.has_matches && (nextSummary.matches_count ?? 0) > 0)

  const openMatchCenter = (view: MatchCenterView) => {
    setMatchCenterView(view)
    setIsDrawerOpen(true)
  }

  const openBadges = async () => {
    if (badgesCount <= 0 && embeddedBadges.length === 0) return

    setIsBadgesOpen(true)
    setLazyBadgesError('')

    if (embeddedBadges.length > 0 || lazyBadges !== null) return

    setIsLoadingLazyBadges(true)
    try {
      const badges = await fetchDashboardBadgesEarnedToday()
      setLazyBadges(badges)
    } catch (error) {
      setLazyBadgesError(error instanceof Error ? error.message : t('Badges konden niet worden geladen.'))
    } finally {
      setIsLoadingLazyBadges(false)
    }
  }

  const handleRetryLazyBadges = async () => {
    setLazyBadgesError('')
    setIsLoadingLazyBadges(true)
    try {
      const badges = await fetchDashboardBadgesEarnedToday()
      setLazyBadges(badges)
    } catch (error) {
      setLazyBadgesError(error instanceof Error ? error.message : t('Badges konden niet worden geladen.'))
    } finally {
      setIsLoadingLazyBadges(false)
    }
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-black tracking-tight text-white sm:text-xl">
              {t('Goedenavond {name}', { name: userLabel })}
            </p>
            <p className="mt-1.5 text-sm text-surface-400 sm:text-base">
              {t('Klaar voor de volgende wedstrijd?')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsPouleCreateOpen(true)}
            className="ml-auto inline-flex min-h-10 shrink-0 items-center justify-center rounded-lg border border-white/[0.12] bg-white/[0.04] px-3.5 py-2 text-sm font-bold text-surface-200 transition hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-950"
          >
            {t('+ Poule')}
          </button>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-2">
            <div className="order-1 space-y-6 lg:order-1">
              {hasPoules === false ? (
                <section className="overflow-hidden rounded-[1.5rem] border border-white/[0.06] bg-[#0d1113]">
                  <div className="bg-[radial-gradient(circle_at_top_left,_rgba(0,126,181,0.18),_transparent_40%)] p-6 sm:p-7">
                    <p className="text-sm font-semibold uppercase tracking-[0.24em] text-accent-400">{t('Home')}</p>
                    <h1 className="mt-3 max-w-2xl text-2xl font-black tracking-tight text-white">{t('Je hebt nog geen poules.')}</h1>
                    <p className="mt-3 max-w-xl text-sm leading-6 text-surface-300 sm:text-base">
                      {t('Zodra je meedoet aan een poule zie je hier direct of je nog iets moet voorspellen.')}
                    </p>
                  </div>
                </section>
              ) : (
                <>
                  <section className="rounded-[1.5rem] border border-white/[0.06] bg-[#0d1113] p-5 sm:p-6">
                    <MatchCenterCard
                      state={matchCenterState}
                      hasLiveMatches={hasLiveMatches}
                      hasPredictionMatches={hasPredictionMatches}
                      onOpenLive={() => openMatchCenter('live')}
                      onOpenPredictions={() => openMatchCenter('predictions')}
                    />
                  </section>

                  <section className="rounded-[1.5rem] border border-white/[0.06] bg-[#0d1113] p-5 sm:p-6">
                    <DayOverviewCard
                      dashboard={dashboard}
                      locale={locale}
                      onOpenRecentPoints={() => setIsRecentPointsOpen(true)}
                      onOpenBadges={() => {
                        void openBadges()
                      }}
                    />
                  </section>
                </>
              )}

              {isFetchingBootstrap ? (
                <p className="text-right text-xs uppercase tracking-[0.2em] text-surface-600">{t('Home wordt ververst…')}</p>
              ) : null}
            </div>

            <div className="order-2 overflow-hidden rounded-[1.25rem] border border-white/[0.07] bg-[#0d1113] lg:order-2">
              <PouleDashboardWidgets
                onOpenPoules={() => setActiveView('poules')}
                onOpenStats={() => setActiveView('stats')}
              />
            </div>
        </div>
      </div>

      {activeView !== 'overview' ? (
        <PouleDashboardSheet
          view={activeView}
          onClose={() => setActiveView('overview')}
          joinedPoules={joinedPoulesQuery.data?.data ?? []}
          joinedPoulesLoading={joinedPoulesQuery.isLoading}
          joinedPoulesError={joinedPoulesQuery.isError}
          statsLocale={locale}
          statsData={myStatsQuery.data}
          statsLoading={myStatsQuery.isLoading}
          statsError={myStatsQuery.isError}
        />
      ) : null}

      {isDrawerOpen ? (
        <MatchCenterDrawer
          locale={locale}
          view={matchCenterView}
          summary={nextSummary}
          liveSummary={liveSummary}
          data={nextMatchesQuery.data}
          liveData={liveMatchesQuery.data}
          selectedDate={selectedDate}
          selectedMatch={selectedMatch}
          predictionMatches={predictionQueue}
          selectedPredictionIndex={selectedPredictionIndex}
          homePred={homePred}
          awayPred={awayPred}
          onClosePrediction={() => {
            if (savePrediction.isPending) return
            savePrediction.reset()
            setSelectedMatch(null)
          }}
          onPickScore={(home, away) => {
            setHomePred(home)
            setAwayPred(away)
            submitPrediction(home, away)
          }}
          onChangeHome={(value) => setHomePred(value)}
          onChangeAway={(value) => setAwayPred(value)}
          onSave={() => {
            if (
              homePred === '' ||
              awayPred === '' ||
              selectedMatch?.representativePouleId == null ||
              !selectedMatch.canPredict
            ) {
              return
            }
            submitPrediction(homePred, awayPred)
          }}
          isSaving={savePrediction.isPending}
          predictionError={savePrediction.error?.message}
          isLoading={nextMatchesQuery.isLoading}
          isFetching={nextMatchesQuery.isFetching}
          isError={nextMatchesQuery.isError}
          isLoadingLive={liveMatchesQuery.isLoading}
          isErrorLive={liveMatchesQuery.isError}
          onClose={() => setIsDrawerOpen(false)}
          onRefresh={() => {
            void Promise.all([
              nextMatchesQuery.refetch(),
              liveMatchesQuery.refetch(),
            ])
          }}
          onSelectDate={setSelectedDate}
          onSelectMatch={(match) => {
            savePrediction.reset()
            setSelectedMatch(match)
          }}
        />
      ) : null}

      {isBadgesOpen ? (
        <BadgesModal
          locale={locale}
          badges={lazyBadges && lazyBadges.length > 0 ? lazyBadges : embeddedBadges}
          totalCount={badgesCount}
          isLoading={isLoadingLazyBadges}
          errorMessage={lazyBadgesError}
          onRetry={() => {
            void handleRetryLazyBadges()
          }}
          onClose={() => {
            if (isLoadingLazyBadges) return
            setIsBadgesOpen(false)
          }}
        />
      ) : null}

      {isRecentPointsOpen ? (
        <RecentPointsModal
          locale={locale}
          dashboard={dashboard}
          data={recentMatchesQuery.data}
          isLoading={recentMatchesQuery.isLoading}
          isFetching={recentMatchesQuery.isFetching}
          isError={recentMatchesQuery.isError}
          onClose={() => setIsRecentPointsOpen(false)}
          onRefresh={() => {
            void recentMatchesQuery.refetch()
          }}
        />
      ) : null}

      {isPouleCreateOpen ? <PouleCreatePage onClose={() => setIsPouleCreateOpen(false)} /> : null}

    </>
  )
}

function PouleDashboardWidgets({
  onOpenPoules,
  onOpenStats,
}: {
  onOpenPoules: () => void
  onOpenStats: () => void
}) {
  const { t } = useTranslation()

  return (
    <nav
      aria-label={t('Poule navigatie')}
      className="overflow-hidden"
    >
      <div className="grid divide-y divide-white/[0.07] sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <button
          type="button"
          onClick={onOpenPoules}
          className="group flex min-w-0 items-center gap-3 p-5 text-left transition hover:bg-[#111719] focus-visible:bg-[#111719] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-400"
        >
          <PouleNavigationIcon type="poules" />
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Overzicht')}</span>
            <span className="mt-1 block truncate text-base font-black tracking-tight text-white">{t('Mijn poules')}</span>
            <span className="mt-1 block text-xs leading-5 text-surface-400">{t('Bekijk je poules, punten en posities.')}</span>
          </span>
          <span className="shrink-0 text-lg text-surface-500 transition group-hover:translate-x-0.5 group-hover:text-white" aria-hidden="true">→</span>
        </button>

        <button
          type="button"
          onClick={onOpenStats}
          className="group flex min-w-0 items-center gap-3 p-5 text-left transition hover:bg-[#111719] focus-visible:bg-[#111719] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-400"
        >
          <PouleNavigationIcon type="stats" />
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Inzicht')}</span>
            <span className="mt-1 block truncate text-base font-black tracking-tight text-white">{t('Mijn stats')}</span>
            <span className="mt-1 block text-xs leading-5 text-surface-400">{t('Bekijk je prestaties en voortgang.')}</span>
          </span>
          <span className="shrink-0 text-lg text-surface-500 transition group-hover:translate-x-0.5 group-hover:text-white" aria-hidden="true">→</span>
        </button>
      </div>
    </nav>
  )
}

function PouleDashboardSheet({
  view,
  onClose,
  joinedPoules,
  joinedPoulesLoading,
  joinedPoulesError,
  statsLocale,
  statsData,
  statsLoading,
  statsError,
}: {
  view: Exclude<DashboardView, 'overview'>
  onClose: () => void
  joinedPoules: JoinedPoule[]
  joinedPoulesLoading: boolean
  joinedPoulesError: boolean
  statsLocale: string
  statsData: MyStatsDashboard | undefined
  statsLoading: boolean
  statsError: boolean
}) {
  const { t } = useTranslation()
  const isPoulesView = view === 'poules'
  const [activePouleTab, setActivePouleTab] = useState<'mine' | 'discover'>('mine')

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Sluit poule-overzicht')}
        className="animate-drawer-backdrop-in absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="poule-dashboard-sheet-title"
        className="animate-manager-player-picker-in absolute inset-x-0 bottom-0 mx-auto flex h-[88dvh] max-h-[88dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-white/[0.07] p-5 sm:p-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">
              {isPoulesView ? (activePouleTab === 'discover' ? t('Publieke poules') : t('Overzicht')) : t('Inzicht')}
            </p>
            <h2 id="poule-dashboard-sheet-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">
              {isPoulesView ? (activePouleTab === 'discover' ? t('Ontdekken') : t('Mijn poules')) : t('Mijn stats')}
            </h2>
          </div>
          <ModalCloseButton onClick={onClose} className="h-9 w-9" />
        </header>

        {isPoulesView ? (
          <div className="shrink-0 border-b border-white/[0.07] px-5 pb-4 pt-3 sm:px-6">
            <div
              className="poule-overview-tablist grid grid-cols-2 rounded-[0.9rem] border border-white/[0.07] bg-[#111416] p-1"
              role="tablist"
              aria-label={t('Poule overzicht')}
            >
              {([
                ['mine', t('Mijn poules')],
                ['discover', t('Ontdekken')],
              ] as const).map(([tab, label]) => {
                const isActive = activePouleTab === tab

                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActivePouleTab(tab)}
                    className={[
                      'poule-overview-tab rounded-[0.7rem] px-3 py-2 text-sm font-bold transition focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111416]',
                      isActive
                        ? 'bg-[#1a2022] text-white shadow-sm'
                        : 'text-surface-500 hover:bg-[#1d2528] hover:text-surface-200 focus-visible:bg-[#1d2528] focus-visible:text-white',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          {isPoulesView && activePouleTab === 'discover' ? (
            <PublicPoulesTab />
          ) : isPoulesView ? (
            <MyPoulesTab
              isLoading={joinedPoulesLoading}
              isError={joinedPoulesError}
              items={joinedPoules}
            />
          ) : (
            <MyStatsTab
              locale={statsLocale}
              isLoading={statsLoading}
              isError={statsError}
              data={statsData}
            />
          )}
        </div>
      </section>
    </div>
  )
}

function PublicPoulesTab() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const normalizedQuery = query.trim()
  const publicPoulesQuery = usePublicPoules(normalizedQuery)
  const results =
    normalizedQuery.length === 1
      ? []
      : publicPoulesQuery.data?.pages.flatMap((page) => page.data) ?? []
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !publicPoulesQuery.hasNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !publicPoulesQuery.isFetchingNextPage) {
          void publicPoulesQuery.fetchNextPage()
        }
      },
      { rootMargin: '240px' },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [
    publicPoulesQuery.fetchNextPage,
    publicPoulesQuery.hasNextPage,
    publicPoulesQuery.isFetchingNextPage,
  ])

  return (
    <div className="space-y-4">
      <label htmlFor="public-poule-search" className="sr-only">
        {t('Zoek een poule')}
      </label>
      <div className="flex items-center gap-3 rounded-[1rem] border border-white/[0.08] bg-[#111719] px-4 py-3 transition focus-within:border-accent-500/60 focus-within:bg-[#151c1e]">
        <svg className="h-5 w-5 shrink-0 text-surface-500" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
          <path d="m16 16 4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        <input
          id="public-poule-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('Zoek een poule...')}
          autoFocus
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-white outline-none placeholder:text-surface-500"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label={t('Wis zoekopdracht')}
            className="text-lg leading-none text-surface-500 transition hover:text-white"
          >
            ×
          </button>
        ) : null}
      </div>

      {normalizedQuery.length > 0 && normalizedQuery.length < 2 ? (
        <p className="text-sm text-surface-500">{t('Typ minimaal 2 letters om te zoeken.')}</p>
      ) : null}

      {publicPoulesQuery.isFetching && !publicPoulesQuery.isFetchingNextPage ? (
        <div role="status" aria-live="polite">
          <span className="sr-only">{t('Publieke poules worden geladen')}</span>
          <div className="h-1 overflow-hidden rounded-full bg-white/[0.08]">
            <div className="animate-ranking-loader h-full w-1/3 rounded-full bg-accent-500" />
          </div>
        </div>
      ) : null}

      {publicPoulesQuery.isError ? (
        <div className="rounded-[1rem] border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-200">
          {t('Publieke poules konden niet worden geladen. Probeer het opnieuw.')}
        </div>
      ) : null}

      {!publicPoulesQuery.isFetching && !publicPoulesQuery.isError && results.length === 0 && normalizedQuery.length !== 1 ? (
        <div className="rounded-[1rem] bg-[#111416] p-5 text-sm text-surface-400">
          {t('Geen poules gevonden.')}
        </div>
      ) : null}

      {results.length > 0 ? (
        <div className="space-y-2.5">
          {results.map((poule) => (
            <PublicPouleSearchCard key={poule.id} poule={poule} />
          ))}
          <div ref={loadMoreRef} className="h-1" aria-hidden="true" />
          {publicPoulesQuery.isFetchingNextPage ? (
            <div role="status" aria-live="polite" className="pt-1">
              <span className="sr-only">{t('Meer publieke poules worden geladen')}</span>
              <div className="h-1 overflow-hidden rounded-full bg-white/[0.08]">
                <div className="animate-ranking-loader h-full w-1/3 rounded-full bg-accent-500" />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function PublicPouleSearchCard({ poule }: { poule: PublicPouleSearchItem }) {
  const { t } = useTranslation()
  return (
    <Link
      to={`/poules/${poule.id}`}
      className="my-poule-card group flex items-center gap-3 rounded-[1rem] bg-[#111416] px-3.5 py-3 transition hover:bg-[#151a1c] focus-visible:bg-[#151a1c] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d1113]"
    >
      <PouleThumb name={poule.name} thumbnailUrl={normalizeString(poule.thumbnail_url)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className="min-w-0 pl-3">
            <h2 className="truncate text-sm font-black text-white">{poule.name}</h2>
            <p className="mt-1 truncate text-xs text-surface-400">
              {normalizeString(poule.description) || t('Openbare poule')}
            </p>
          </div>
          <span className="ml-auto shrink-0 text-lg text-surface-500 transition group-hover:translate-x-0.5 group-hover:text-white">›</span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <MiniStat
            label={t('Leden')}
            value={formatPoulesNumber(poule.members_count)}
            plain
            className="border-l border-white/[0.08]"
          />
          <MiniStat
            label={t('Status')}
            value={poule.is_member ? t('Lid') : poule.requires_approval ? t('Aanvraag nodig') : t('Openbaar')}
            plain
            className="border-l border-white/[0.08]"
          />
        </div>
      </div>
    </Link>
  )
}

function PouleNavigationIcon({ type }: { type: 'poules' | 'stats' }) {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#151a1c] text-accent-300" aria-hidden="true">
      {type === 'poules' ? (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
          <path d="M5 5.5h14v13H5zM8 9h8M8 12h8M8 15h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
          <path d="M5 19V9M12 19V5M19 19v-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M3.5 19.5h17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      )}
    </span>
  )
}

function MatchCenterCard({
  state,
  hasLiveMatches,
  hasPredictionMatches,
  onOpenLive,
  onOpenPredictions,
}: {
  state: ReturnType<typeof getMatchCenterState>
  hasLiveMatches: boolean
  hasPredictionMatches: boolean
  onOpenLive: () => void
  onOpenPredictions: () => void
}) {
  const { t } = useTranslation()

  return (
    <section>
      <p className="text-base font-bold text-surface-300 sm:text-lg">{t('MatchCenter')}</p>
      <div className="mt-3 flex items-start gap-2.5">
        <div
          className={[
            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base font-bold sm:h-9 sm:w-9 sm:text-lg',
            state.tone === 'ready'
              ? 'bg-emerald-400/15 text-emerald-300'
              : state.tone === 'live'
                ? 'bg-rose-500/15 text-rose-300'
                : state.tone === 'action'
                  ? 'bg-accent-500/15 text-accent-300'
                  : 'bg-surface-800 text-surface-400',
          ].join(' ')}
        >
          {state.icon}
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-black tracking-tight text-white sm:text-xl">
            {state.title}
          </h2>
          <p className="mt-1.5 text-xs text-surface-400 sm:text-sm">{state.subtitle}</p>
        </div>
      </div>

      <div className="app-progress-track mt-4 h-2 rounded-full bg-[#171a1c]">
        <div
          className={[
            'h-full rounded-full transition-all',
            state.tone === 'ready'
              ? 'bg-emerald-400'
              : state.tone === 'live'
                ? 'bg-rose-400'
                : state.tone === 'action'
                  ? 'bg-accent-500'
                  : 'bg-surface-700',
          ].join(' ')}
          style={{ width: `${state.progress}%` }}
        />
      </div>

      <p className="mt-3 text-xs font-semibold text-surface-300 sm:text-sm">{state.meta}</p>
      <div className={`mt-4 grid gap-2 ${hasLiveMatches && hasPredictionMatches ? 'sm:grid-cols-2' : ''}`}>
        {hasLiveMatches ? (
          <button
            type="button"
            onClick={onOpenLive}
            className="group inline-flex w-full items-center justify-between whitespace-nowrap rounded-[1rem] bg-accent-600 px-4 py-3 text-left text-xs font-bold text-white transition hover:bg-accent-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 sm:text-sm"
          >
            {t('Live wedstrijden')}
            <span className="text-lg text-white transition-transform duration-200 group-hover:translate-x-1">→</span>
          </button>
        ) : null}
        {hasPredictionMatches ? (
          <button
            type="button"
            onClick={onOpenPredictions}
            className={`${hasLiveMatches ? 'border border-white/[0.12] bg-[#151a1c] hover:bg-white/[0.09]' : 'bg-accent-600 hover:bg-accent-500'} group inline-flex w-full items-center justify-between whitespace-nowrap rounded-[1rem] px-4 py-3 text-left text-sm font-bold text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 sm:text-base`}
          >
            {t('Voorspel wedstrijden')}
            <span className={`prediction-cta-arrow text-lg transition-transform duration-200 group-hover:translate-x-1 ${hasLiveMatches ? 'text-surface-300 group-hover:text-white' : 'text-white'}`}>→</span>
          </button>
        ) : null}
      </div>
    </section>
  )
}

function DayOverviewCard({
  dashboard,
  locale,
  onOpenRecentPoints,
  onOpenBadges,
}: {
  dashboard: HomeDashboard | undefined
  locale: string
  onOpenRecentPoints: () => void
  onOpenBadges: () => void
}) {
  const { t } = useTranslation()
  const hasRecentMatches = (dashboard?.matches_count ?? 0) > 0
  const badgesCount = getDashboardBadgesCount(dashboard)
  const hasBadges = badgesCount > 0 || getDashboardBadges(dashboard).length > 0

  return (
    <section>
      <h2 className="text-lg font-black tracking-tight text-white sm:text-xl">
        {t('Dagoverzicht')}
      </h2>
      <p className="mt-1 text-sm text-surface-400">{t('Overzicht van vandaag')}</p>

      <button
        type="button"
        onClick={onOpenRecentPoints}
        disabled={!hasRecentMatches}
        className={[
          'mt-5 w-full rounded-[1.1rem] bg-[#111416] px-4 py-4 text-left',
          hasRecentMatches ? 'transition hover:bg-[#171a1c]' : 'cursor-default',
        ].join(' ')}
      >
        <p className="text-sm text-surface-400 sm:text-base">
          {dashboard?.points_date
            ? formatDate(dashboard.points_date, locale)
            : dashboard?.date
              ? formatDate(dashboard.date, locale)
              : t('Vandaag')}
        </p>
        <div className="mt-1.5 flex items-center justify-between gap-4">
          <p className="text-lg font-black text-white sm:text-xl">
            {t('{points} verdiend', { points: formatPoints(dashboard?.total_points) })}
          </p>
          {hasRecentMatches ? (
            <span className="text-xl text-surface-500">›</span>
          ) : null}
        </div>
      </button>

      <div className="mt-5 grid grid-cols-3 divide-x divide-white/[0.06] rounded-[1.1rem] bg-[#111416] px-2 py-3 text-center">
        <MetricBlock
          value={formatCount(dashboard?.matches_count)}
          label={t('wedstrijden')}
        />
        <MetricBlock
          value={formatCount(dashboard?.prediction_counts?.perfect)}
          label={t('perfect')}
        />
        <MetricBlock
          value={formatCount(dashboard?.prediction_counts?.wrong)}
          label={t('volledig fout')}
        />
      </div>

      {hasBadges ? (
        <button
          type="button"
          onClick={onOpenBadges}
          className="mt-4 flex w-full items-center justify-between rounded-[1.1rem] bg-[#111416] px-4 py-3.5 text-left transition hover:bg-[#171a1c]"
        >
          <div className="flex items-center gap-3">
            <span className="text-xl text-surface-300">🏆</span>
            <div>
              <span className="block text-base font-bold text-surface-200 sm:text-lg">
                {t('Badges verdiend')}
              </span>
              <span className="mt-0.5 block text-xs text-surface-500 sm:text-sm">
                {t('Bekijk welke badges je vandaag hebt vrijgespeeld')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="rounded-full bg-surface-700 px-3 py-1.5 text-sm font-black text-white sm:text-base">
              {formatCount(badgesCount)}
            </span>
            <span className="text-lg text-surface-500">›</span>
          </div>
        </button>
      ) : null}
    </section>
  )
}

function BadgesModal({
  locale,
  badges,
  totalCount,
  isLoading,
  errorMessage,
  onRetry,
  onClose,
}: {
  locale: string
  badges: HomeBadge[]
  totalCount?: number | undefined
  isLoading: boolean
  errorMessage?: string | undefined
  onRetry: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const sortedBadges = useMemo(
    () =>
      [...badges].sort(
        (left, right) =>
          getMatchTimeValue(right.earned_at) - getMatchTimeValue(left.earned_at),
      ),
    [badges],
  )
  const hasBadges = sortedBadges.length > 0

  useEffect(() => {
    setActiveIndex(0)
    const viewport = viewportRef.current
    if (!viewport) return
    viewport.scrollTo({ left: 0, behavior: 'auto' })
  }, [sortedBadges.length])

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && !(isLoading && !hasBadges)) onClose()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [hasBadges, isLoading, onClose])

  const handleScroll = () => {
    const viewport = viewportRef.current
    if (!viewport || viewport.clientWidth === 0) return
    const nextIndex = Math.round(viewport.scrollLeft / viewport.clientWidth)
    setActiveIndex(Math.max(0, Math.min(sortedBadges.length - 1, nextIndex)))
  }

  const scrollByCard = (direction: -1 | 1) => {
    const viewport = viewportRef.current
    if (!viewport) return
    viewport.scrollBy({
      left: viewport.clientWidth * direction,
      behavior: 'smooth',
    })
  }

  return (
    <div className="fixed inset-0 z-[55]">
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading && !hasBadges}
        aria-label={t('Sluit badge-overzicht')}
        className="animate-drawer-backdrop-in absolute inset-0 bg-black/85 backdrop-blur-sm disabled:cursor-default"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-badges-title"
        className="animate-manager-player-picker-in absolute inset-x-0 bottom-0 mx-auto flex h-[92dvh] max-h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#060707] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]"
      >
        <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">
          <header className="flex shrink-0 items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
                {t('Badges')}
              </p>
              <h2 id="dashboard-badges-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">
                {t('Badges verdiend vandaag')}
              </h2>
              <p className="mt-1 text-sm text-surface-400">
                {hasBadges
                  ? t('{count} vandaag zichtbaar', { count: formatCount(totalCount ?? sortedBadges.length) })
                  : isLoading
                    ? t('Badges worden geladen')
                    : t('Nog geen badges beschikbaar voor vandaag')}
              </p>
            </div>
            <ModalCloseButton
              onClick={onClose}
              disabled={isLoading && !hasBadges}
              label={t('Sluiten')}
            />
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto pt-4">

          {isLoading && !hasBadges ? (
            <div className="mt-4 rounded-[1rem] bg-[#111416] px-4 py-8 text-center">
              <p className="text-base font-bold text-white">{t('Badges laden…')}</p>
              <p className="mt-2 text-sm text-surface-400">
                {t('We halen je vandaag verdiende badges op.')}
              </p>
            </div>
          ) : null}

          {!isLoading && !hasBadges && errorMessage ? (
            <div className="mt-4 rounded-[1rem] border border-red-900/60 bg-red-950/30 px-4 py-5 text-center">
              <p className="text-base font-bold text-red-100">{t('Badges niet geladen')}</p>
              <p className="mt-2 text-sm text-red-100/80">{errorMessage}</p>
              <button
                type="button"
                onClick={onRetry}
                className="mt-4 rounded-full border border-red-200/20 px-4 py-2 text-sm font-semibold text-red-100 transition hover:bg-white/5"
              >
                {t('Opnieuw proberen')}
              </button>
            </div>
          ) : null}

          {hasBadges ? (
            <>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => scrollByCard(-1)}
                  disabled={activeIndex <= 0}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.85rem] border border-[#202427] bg-[#151a1c] text-sm text-surface-300 transition hover:border-surface-600 hover:text-white disabled:opacity-35"
                >
                  ‹
                </button>

                <div
                  ref={viewportRef}
                  onScroll={handleScroll}
                  className="no-scrollbar flex flex-1 snap-x snap-mandatory overflow-x-auto"
                >
                  {sortedBadges.map((badge) => (
                    <article
                      key={`badge-${badge.id ?? badge.badge_id ?? badge.badge_key ?? badge.name}`}
                      className="min-w-full snap-center pr-0.5"
                    >
                      <div className="rounded-[1rem] bg-[#111416] p-4">
                        <div className="flex items-start gap-3">
                          <BadgeVisual badge={badge} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-lg font-black text-white">
                                {getBadgeName(badge)}
                              </h3>
                              {getBadgeTierLabel(badge) ? (
                                <span className="rounded-full bg-accent-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent-300">
                                  {getBadgeTierLabel(badge)}
                                </span>
                              ) : null}
                              {getBadgeDifficultyLabel(badge) ? (
                                <span className="rounded-full border border-surface-700 bg-surface-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-300">
                                  {getBadgeDifficultyLabel(badge)}
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-1 text-sm text-surface-400">
                              {badge.reason ??
                                badge.badge?.description ??
                                badge.description ??
                                'Badge behaald.'}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                          <BadgeMetaCard
                            label={t('Behaald op')}
                            value={
                              badge.earned_at
                                ? formatDateTime(badge.earned_at, locale)
                                : t('Onbekend')
                            }
                          />
                          <BadgeMetaCard
                            label={t('Categorie')}
                            value={badge.category ?? badge.badge?.category ?? t('Trofee')}
                          />
                          {getBadgePouleLabel(badge) ? (
                            <BadgeMetaCard
                              label={t('Poule')}
                              value={getBadgePouleLabel(badge) ?? t('Onbekend')}
                            />
                          ) : null}
                          {getBadgeContextLabel(badge) ? (
                            <BadgeMetaCard
                              label={t('Context')}
                              value={getBadgeContextLabel(badge) ?? t('Onbekend')}
                            />
                          ) : null}
                          {badge.round?.name ? (
                            <BadgeMetaCard
                              label={t('Ronde')}
                              value={badge.round.name}
                            />
                          ) : null}
                        </div>

                        {getBadgeRelatedMatches(badge).length > 0 ? (
                          <div className="mt-4">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
                              {t('Gerelateerde wedstrijden')}
                            </p>
                            <div className="mt-2 space-y-2">
                              {getBadgeRelatedMatches(badge).map((match) => (
                                <BadgeMatchCard
                                  key={`badge-match-${badge.id ?? badge.badge_id}-${match.id}`}
                                  locale={locale}
                                  match={match}
                                />
                              ))}
                            </div>
                          </div>
                        ) : null}

                        <div className="mt-4 flex items-center justify-between gap-3 rounded-[0.9rem] bg-[#111416] px-3 py-2.5">
                          <div>
                            <p className="text-sm font-semibold text-white">{t('Deel badge')}</p>
                            <p className="mt-1 text-xs text-surface-500">
                              {t('Share volgt later, maar de plek staat klaar.')}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="rounded-full border border-[#202427] px-3 py-1.5 text-xs font-semibold text-surface-300 transition hover:border-surface-600 hover:text-white"
                          >
                            {t('Delen')}
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => scrollByCard(1)}
                  disabled={activeIndex >= sortedBadges.length - 1}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.85rem] border border-[#202427] bg-[#151a1c] text-sm text-surface-300 transition hover:border-surface-600 hover:text-white disabled:opacity-35"
                >
                  ›
                </button>
              </div>

              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  {sortedBadges.map((badge, index) => (
                    <span
                      key={`badge-dot-${badge.id ?? badge.badge_id ?? index}`}
                      className={[
                        'h-2 rounded-full transition-all',
                        index === activeIndex
                          ? 'w-5 bg-accent-600'
                          : 'w-2 bg-surface-700',
                      ].join(' ')}
                    />
                  ))}
                </div>
                <p className="text-xs font-semibold text-surface-500">
                  {activeIndex + 1} / {sortedBadges.length}
                </p>
              </div>
            </>
          ) : (
            <div className="mt-4 rounded-[1rem] bg-[#111416] px-4 py-8 text-center">
              <p className="text-base font-bold text-white">{t('Nog geen badges verdiend')}</p>
              <p className="mt-2 text-sm text-surface-400">
                {t('Zodra je een badge vrijspeelt, zie je hem hier terug.')}
              </p>
            </div>
          )}

          </div>
        </div>
      </section>
    </div>
  )
}

function BadgeVisual({ badge }: { badge: HomeBadge }) {
  const visualUrl = getBadgeVisualUrl(badge)

  if (visualUrl) {
    return (
      <img
        src={visualUrl}
        alt=""
        className="h-16 w-16 shrink-0 rounded-[1rem] object-cover"
      />
    )
  }

  return (
    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1rem] bg-[#0d1520] text-2xl">
      🏆
    </span>
  )
}

function BadgeMatchCard({
  locale,
  match,
}: {
  locale: string
  match: HomeBadgeMatch
}) {
  return (
    <div className="rounded-[1rem] bg-[#090c0e] px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="w-14 shrink-0 pt-0.5 text-center text-xs font-bold text-white">
          {match.kickoff ? formatTime(match.kickoff, locale) : '--:--'}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-2.5">
            <RecentTeamLine
              name={match.homeName}
              logoUrl={match.homeLogo}
              score={match.homeScore}
              prediction={match.homePred}
              pointsBadgeValue={
                match.homePred != null || match.awayPred != null
                  ? match.pointsAwarded
                  : undefined
              }
            />
            <RecentTeamLine
              name={match.awayName}
              logoUrl={match.awayLogo}
              score={match.awayScore}
              prediction={match.awayPred}
            />
          </div>

        </div>
      </div>
    </div>
  )
}

function BadgeMetaCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[0.9rem] bg-[#111416] px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-white">{value}</p>
    </div>
  )
}

function RecentPointsModal({
  locale,
  dashboard,
  data,
  isLoading,
  isFetching,
  isError,
  onClose,
  onRefresh,
}: {
  locale: string
  dashboard: HomeDashboard | undefined
  data: HomeRecentMatches | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  onClose: () => void
  onRefresh: () => void
}) {
  const { t } = useTranslation()
  const groups = useMemo(
    () => getRecentPointGroups(data?.poules, dashboard?.poules),
    [data?.poules, dashboard?.poules],
  )
  const hasMatches = groups.some((group) => group.matches.length > 0)

  return (
    <div className="animate-drawer-backdrop-in fixed inset-0 z-[55] bg-black/85 backdrop-blur-sm">
      <div className="flex h-full items-start justify-center overflow-y-auto p-4 pt-8 lg:items-center">
        <div className="recent-points-modal animate-modal-panel-in w-full max-w-2xl rounded-[1.1rem] border border-[#202427] bg-[#060707] p-4 shadow-[0_18px_56px_rgba(0,0,0,0.55)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
                {t('Recente punten')}
              </p>
              <h2 className="mt-1 text-xl font-black tracking-tight text-white">
                {t('{points} verdiend', { points: formatPoints(dashboard?.total_points) })}
              </h2>
              <p className="mt-1 text-sm text-surface-400">
                {dashboard?.points_date
                  ? formatDate(dashboard.points_date, locale)
                  : dashboard?.date
                    ? formatDate(dashboard.date, locale)
                    : t('Laatste speeldag')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onRefresh}
                className="rounded-lg border border-surface-700 px-2.5 py-1.5 text-[11px] text-surface-300 transition hover:border-surface-500 hover:text-white"
              >
                {t('Ververs')}
              </button>
              <ModalCloseButton onClick={onClose} />
            </div>
          </div>

          <div className="recent-points-stats mt-4 grid grid-cols-3 divide-x divide-white/[0.06] rounded-[1rem] bg-[#111416] px-2 py-3 text-center">
            <MetricBlock value={formatCount(dashboard?.matches_count)} label={t('wedstrijden')} />
            <MetricBlock
              value={formatCount(dashboard?.prediction_counts?.perfect)}
              label={t('perfect')}
            />
            <MetricBlock
              value={formatCount(dashboard?.prediction_counts?.wrong)}
              label={t('volledig fout')}
            />
          </div>

          {isFetching ? (
            <div className="recent-points-state mt-4 rounded-[1rem] bg-[#111416] p-4">
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 animate-pulse rounded-full bg-accent-600" />
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-surface-400">
                  {t('Recente wedstrijden laden…')}
                </p>
              </div>
            </div>
          ) : null}

          {isError ? (
            <div className="recent-points-error mt-4 rounded-[1rem] border border-red-900/60 bg-red-950/30 p-4 text-xs text-red-200 sm:text-sm">
              {t('Recente wedstrijden konden niet worden geladen.')}
            </div>
          ) : null}

          {!isError && !isLoading && !hasMatches ? (
            <div className="recent-points-state mt-4 rounded-[1rem] bg-[#111416] p-4 text-sm text-surface-400">
              {t('Geen recente wedstrijden gevonden.')}
            </div>
          ) : null}

          {!isError && hasMatches ? (
            <div className="mt-4 max-h-[28rem] space-y-3 overflow-y-auto pr-1">
              {groups.map((group) => (
                <section key={`recent-${group.id}`} className="space-y-2">
                  <div className="match-center-group-header flex items-center gap-2.5 bg-[#090909] px-2.5 py-1.5">
                    <PouleLogo label={group.label} thumbnailUrl={group.thumbnailUrl} />
                    <div className="min-w-0">
                      <h3 className="truncate text-xs font-black text-white sm:text-sm">
                        {group.label}
                      </h3>
                      <p className="text-[10px] text-surface-500">
                        {t('{count} wedstrijden', { count: formatCount(group.matches.length) })}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {group.matches.map((match) => (
                      <RecentPointsRowCard
                        key={`recent-${group.id}-${match.id}`}
                        locale={locale}
                        match={match}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : null}

        </div>
      </div>
    </div>
  )
}

function RecentPointsRowCard({
  locale,
  match,
}: {
  locale: string
  match: RecentDisplayMatch
}) {
  return (
    <div className="recent-points-match-card rounded-[1rem] bg-[#090c0e] px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="w-14 shrink-0 pt-0.5 text-center text-xs font-bold text-white">
          {match.kickoff ? formatTime(match.kickoff, locale) : '--:--'}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-2.5">
            <RecentTeamLine
              name={match.homeName}
              logoUrl={match.homeLogo}
              score={match.homeScore}
              prediction={match.homePred}
              pointsBadgeValue={
                match.homePred != null || match.awayPred != null
                  ? match.pointsAwarded
                  : undefined
              }
            />
            <RecentTeamLine
              name={match.awayName}
              logoUrl={match.awayLogo}
              score={match.awayScore}
              prediction={match.awayPred}
            />
          </div>

        </div>
      </div>
    </div>
  )
}

function RecentTeamLine({
  name,
  logoUrl,
  score,
  prediction,
  pointsBadgeValue,
}: {
  name: string
  logoUrl: string | undefined
  score: number | undefined
  prediction?: number | undefined
  pointsBadgeValue?: number | undefined
}) {
  return (
    <div className="flex items-center gap-2">
      <TeamLogo name={name} logoUrl={logoUrl} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-surface-100 sm:text-base">{name}</span>
      {typeof pointsBadgeValue === 'number' ? <PointsBadge points={pointsBadgeValue} /> : null}
      {typeof prediction === 'number' ? (
        <span className="min-w-[1.5rem] text-right text-sm font-bold text-accent-300">
          {prediction}
        </span>
      ) : null}
      <span className="min-w-[1.5rem] text-right text-base font-black text-white">
        {score != null ? score : '–'}
      </span>
    </div>
  )
}

function MatchCenterDrawer({
  locale,
  view,
  summary,
  liveSummary,
  data,
  liveData,
  selectedDate,
  selectedMatch,
  predictionMatches,
  selectedPredictionIndex,
  homePred,
  awayPred,
  onClosePrediction,
  onPickScore,
  onChangeHome,
  onChangeAway,
  onSave,
  isSaving,
  predictionError,
  isLoading,
  isFetching,
  isError,
  isLoadingLive,
  isErrorLive,
  onClose,
  onRefresh,
  onSelectDate,
  onSelectMatch,
}: {
  locale: string
  view: MatchCenterView
  summary: HomeNextSummary | undefined
  liveSummary: HomeLiveSummary | undefined
  data: MatchesData | undefined
  liveData: LiveMatchesData | undefined
  selectedDate: string | undefined
  selectedMatch: DisplayMatch | null
  predictionMatches: DisplayMatch[]
  selectedPredictionIndex: number
  homePred: number | ''
  awayPred: number | ''
  onClosePrediction: () => void
  onPickScore: (home: number, away: number) => void
  onChangeHome: (value: number | '') => void
  onChangeAway: (value: number | '') => void
  onSave: () => void
  isSaving: boolean
  predictionError: string | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  isLoadingLive: boolean
  isErrorLive: boolean
  onClose: () => void
  onRefresh: () => void
  onSelectDate: (date: string | undefined) => void
  onSelectMatch: (match: DisplayMatch) => void
}) {
  const { t } = useTranslation()
  const deduplicatedVisible = false
  const tabsViewportRef = useRef<HTMLDivElement | null>(null)
  const pouleGroups = useMemo(() => getPouleGroups(data), [data])
  const livePouleGroups = useMemo(() => getLivePouleGroups(liveData), [liveData])
  const multiPouleBanner = useMemo(
    () => getMultiPouleBannerText(data),
    [data],
  )
  const hasContent = pouleGroups.some((group) => group.matches.length > 0)

  useEffect(() => {
    const viewport = tabsViewportRef.current
    if (!viewport) return

    const activeTab = viewport.querySelector<HTMLElement>('[data-day-tab-active="true"]')
    if (!activeTab) return

    const viewportRect = viewport.getBoundingClientRect()
    const activeRect = activeTab.getBoundingClientRect()
    const targetLeft =
      viewport.scrollLeft +
      (activeRect.left - viewportRect.left) -
      (viewportRect.width / 2 - activeRect.width / 2)

    viewport.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: 'smooth',
    })
  }, [data?.selected_tab_id, data?.tabs, selectedDate])

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Sluit wedstrijden')}
        className="animate-drawer-backdrop-in absolute inset-0 bg-black/80 backdrop-blur-sm"
      />

      <aside className="match-center-drawer animate-drawer-panel-in absolute inset-x-0 bottom-0 top-16 overflow-hidden rounded-t-[1.5rem] border-t border-[#1c2330] bg-black shadow-[0_-20px_64px_rgba(0,0,0,0.5)] lg:inset-y-0 lg:left-auto lg:right-0 lg:top-0 lg:w-[27rem] xl:w-[29rem] lg:rounded-none lg:border-l lg:border-t-0">
        <div className="flex h-full flex-col">
          <div className="match-center-drawer-header flex items-center justify-between gap-4 border-b border-surface-800 px-4 py-3.5">
            <div className="min-w-0">
              <h2 className="text-lg font-black tracking-tight text-white sm:text-xl">
                {view === 'live' ? t('Live wedstrijden') : t('Te voorspellen wedstrijden')}
              </h2>
              <p className="mt-1 text-xs text-surface-400 sm:text-sm">
                {view === 'predictions' && summary?.unpredicted_count
                  ? t('Nog {count} voorspellingen open', { count: summary.unpredicted_count })
                  : view === 'live'
                    ? t('Bekijk de wedstrijden die nu bezig zijn')
                    : t('Bekijk en beheer je wedstrijden')}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ModalCloseButton onClick={onClose} />
            </div>
          </div>

          <div className="no-scrollbar min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-3.5 py-3">
            <div className="space-y-3">
              {view === 'live' ? (
                <LiveMatchesSection
                  locale={locale}
                  summary={liveSummary}
                  groups={livePouleGroups}
                  isLoading={isLoadingLive}
                  isError={isErrorLive}
                  onRefresh={onRefresh}
                  showEmpty
                />
              ) : null}

              {view === 'predictions' && selectedMatch ? (
                <PredictionCarousel
                  key={getDisplayMatchKey(selectedMatch)}
                  locale={locale}
                  match={selectedMatch}
                  currentIndex={selectedPredictionIndex}
                  total={predictionMatches.length}
                  onBack={onClosePrediction}
                  onPrevious={() => {
                    const previous = predictionMatches[selectedPredictionIndex - 1]
                    if (previous) onSelectMatch(previous)
                  }}
                  onNext={() => {
                    const next = predictionMatches[selectedPredictionIndex + 1]
                    if (next) onSelectMatch(next)
                  }}
                  homePred={homePred}
                  awayPred={awayPred}
                  onPickScore={onPickScore}
                  onChangeHome={onChangeHome}
                  onChangeAway={onChangeAway}
                  onSave={onSave}
                  isSaving={isSaving}
                  errorMessage={predictionError}
                />
              ) : null}

              {view === 'predictions' && !selectedMatch && data?.tabs.length ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!data?.can_go_previous || !data?.previous_date}
                    onClick={() => onSelectDate(normalizeOptionalString(data?.previous_date))}
                    className="match-center-day-arrow flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.85rem] border border-[#202427] bg-[#151a1c] text-sm text-surface-300 transition hover:border-surface-600 hover:text-white disabled:opacity-35 disabled:hover:border-[#202427] disabled:hover:text-surface-300"
                  >
                    ‹
                  </button>

                  <div
                    ref={tabsViewportRef}
                    className="no-scrollbar flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-1 pt-0.5"
                  >
                    {data.tabs.map((tab) => (
                      (() => {
                        const isActive =
                          (selectedDate != null &&
                            normalizeOptionalString(tab.date) === selectedDate) ||
                          (selectedDate == null &&
                            (tab.is_selected || data.selected_tab_id === tab.id))

                        return (
                          <button
                            key={tab.id}
                            type="button"
                            data-day-tab-active={isActive ? 'true' : 'false'}
                            onClick={() => {
                              const nextDate = normalizeOptionalString(tab.date)
                              if (!nextDate) return
                              onSelectDate(nextDate)
                            }}
                            className={[
                              'match-center-day-tab min-w-[5.6rem] shrink-0 rounded-[0.85rem] border px-2.5 py-1.5 text-left transition',
                              isActive
                                ? 'border-accent-500 bg-accent-600 text-white shadow-[0_10px_24px_rgba(0,126,181,0.28)]'
                                : 'border-[#202427] bg-[#151a1c] text-surface-300',
                            ].join(' ')}
                          >
                            <p className="text-xs font-semibold">
                              {tab.short_label ?? tab.label ?? tab.date ?? tab.id}
                            </p>
                            <div className="mt-1 flex items-center justify-between text-[11px]">
                              <span>{formatCount(tab.match_count)}</span>
                              <span
                                className={[
                                  'h-3 w-3 rounded-full',
                                  tab.has_unpredicted
                                    ? 'bg-amber-400'
                                    : 'bg-emerald-400',
                                ].join(' ')}
                              />
                            </div>
                          </button>
                        )
                      })()
                    ))}
                  </div>

                  <button
                    type="button"
                    disabled={!data?.can_go_next || !data?.next_date}
                    onClick={() => onSelectDate(normalizeOptionalString(data?.next_date))}
                    className="match-center-day-arrow flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.85rem] border border-[#202427] bg-[#151a1c] text-sm text-surface-300 transition hover:border-surface-600 hover:text-white disabled:opacity-35 disabled:hover:border-[#202427] disabled:hover:text-surface-300"
                  >
                    ›
                  </button>
                </div>
              ) : null}
            </div>

            {view === 'predictions' && !selectedMatch && data?.tabs.length ? (
              <div
                className="-mx-3.5 mt-3 border-t border-surface-800/80"
                aria-hidden="true"
              />
            ) : null}

            {view === 'predictions' && !selectedMatch ? (
              <div
                className="-mx-3.5 mb-3 h-px sm:-mx-5"
                role={isLoading || isFetching ? 'status' : undefined}
                aria-live={isLoading || isFetching ? 'polite' : undefined}
                aria-hidden={isLoading || isFetching ? undefined : true}
              >
                {isLoading || isFetching ? (
                  <>
                    <span className="sr-only">{t('Wedstrijden worden geladen')}</span>
                    <div className="h-px w-full overflow-hidden bg-white/[0.08]">
                      <div className="animate-matches-loading-bar h-full w-1/3 rounded-full bg-accent-500" />
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-3">
              {view === 'predictions' && !selectedMatch && multiPouleBanner ? (
                <div className="match-center-multi-poule-banner flex items-center justify-between rounded-[1rem] border border-[#222a38] bg-[#0d0f13] px-3.5 py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="text-lg text-surface-400">ⓘ</span>
                    <span className="text-sm font-semibold text-surface-300">
                      {multiPouleBanner}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-accent-400">{t('Wijzigen')}</span>
                </div>
              ) : null}

              {view === 'predictions' && !selectedMatch && !isFetching && isError ? (
                <div className="rounded-[1rem] border border-red-900/60 bg-red-950/30 p-4 text-xs text-red-200 sm:text-sm">
                  {t('Wedstrijden konden niet worden geladen.')}
                </div>
              ) : null}

              {view === 'predictions' && !selectedMatch && !isFetching && !isLoading && !isError && !hasContent ? (
                <div className="rounded-[1rem] bg-[#111416] p-4 text-xs text-surface-400 sm:text-sm">
                  {t('Geen wedstrijden gevonden.')}
                </div>
              ) : null}

              {view === 'predictions' && !selectedMatch && !isFetching && !isLoading && !isError && !deduplicatedVisible ? (
                <div className="space-y-3">
                  {(() => {
                    let rowIndex = 0
                    return pouleGroups.map((group) => (
                      <section key={group.id} className="space-y-2">
                        <div className="match-center-group-header flex items-center gap-2.5 bg-[#090909] px-2.5 py-1.5">
                          <PouleLogo label={group.label} thumbnailUrl={group.thumbnailUrl} />
                          <h3 className="text-xs font-black text-white sm:text-sm">
                            {group.label}
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {group.matches.map((match) => {
                            const delay = rowIndex * 40
                            rowIndex += 1
                            return (
                              <div
                                key={`${group.id}-${match.id}`}
                                className="animate-match-row-in"
                                style={{ animationDelay: `${delay}ms` }}
                              >
                                <MatchRowCard
                                  match={match}
                                  locale={locale}
                                  onClick={() => onSelectMatch(match)}
                                />
                              </div>
                            )
                          })}
                        </div>
                      </section>
                    ))
                  })()}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </aside>
    </div>
  )
}

function LiveMatchesSection({
  locale,
  summary,
  groups,
  isLoading,
  isError,
  onRefresh,
  showEmpty = false,
}: {
  locale: string
  summary: HomeLiveSummary | undefined
  groups: Array<{
    id: number
    label: string
    thumbnailUrl: string | undefined
    matches: LiveDisplayMatch[]
  }>
  isLoading: boolean
  isError: boolean
  onRefresh: () => void
  showEmpty?: boolean
}) {
  const { t } = useTranslation()
  const liveCount = summary?.matches_count ?? groups.reduce((count, group) => count + group.matches.length, 0)
  const hasLive = getHasLiveMatches(summary) || groups.some((group) => group.matches.length > 0)

  if (isLoading && !showEmpty) {
    return null
  }

  if (isLoading && showEmpty) {
    return (
      <div
        className="h-1 w-full overflow-hidden rounded-full bg-white/[0.08]"
        role="status"
        aria-label={t('Live wedstrijden laden…')}
      >
        <div className="animate-matches-loading-bar h-full w-1/3 rounded-full bg-accent-500" />
      </div>
    )
  }

  if (!isError && !hasLive && showEmpty) {
    return (
      <div className="rounded-[1rem] border border-white/[0.07] bg-[#111416] p-5 text-sm text-surface-400">
        {t('Er zijn nu geen live wedstrijden in deze poule.')}
      </div>
    )
  }

  if (!isError && !hasLive) {
    return null
  }

  return (
    <section className="space-y-2.5">
      <div className="rounded-[0.9rem] border border-[#3d1020] bg-[linear-gradient(180deg,_rgba(44,9,20,0.92),_rgba(21,7,14,0.96))] px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-300">
              {t('Live nu')}
            </p>
            <p className="mt-1 text-sm font-black text-white">
              {isLoading
                ? t('Live wedstrijden laden…')
                : t('{count} actief', { count: formatCount(liveCount) })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-200">
              live
            </span>
            <button
              type="button"
              onClick={onRefresh}
              className="rounded-lg border border-rose-400/20 px-2.5 py-1 text-[11px] text-rose-100 transition hover:border-rose-300/40 hover:text-white"
            >
              {t('Ververs')}
            </button>
          </div>
        </div>
      </div>

      {isError ? (
        <div className="rounded-[0.9rem] border border-red-900/60 bg-red-950/30 p-3 text-xs text-red-200">
          {t('Live wedstrijden konden niet worden geladen.')}
        </div>
      ) : null}

      {!isLoading && !isError ? (
        <div className="space-y-3">
          {groups.map((group) => (
            <section key={`live-${group.id}`} className="space-y-2">
              <div className="match-center-group-header flex items-center gap-2.5 bg-[#090909] px-2.5 py-1.5">
                <PouleLogo label={group.label} thumbnailUrl={group.thumbnailUrl} />
                <div className="min-w-0">
                  <h3 className="truncate text-xs font-black text-white sm:text-sm">
                    {group.label}
                  </h3>
                  <p className="text-[10px] text-surface-500">
                    {t('{count} live', { count: formatCount(group.matches.length) })}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {group.matches.map((match) => (
                  <LiveMatchRowCard
                    key={`live-${group.id}-${match.id}`}
                    locale={locale}
                    match={match}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </section>
  )
}

function LiveMatchRowCard({
  locale,
  match,
}: {
  locale: string
  match: LiveDisplayMatch
}) {
  const { t } = useTranslation()
  return (
    <Link
      to={getLiveMatchDetailPath(match)}
      className="block rounded-[1rem] bg-rose-500/[0.06] px-4 py-3 transition hover:bg-rose-500/[0.1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
      aria-label={`${match.homeName} - ${match.awayName}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-14 shrink-0 pt-0.5 text-center">
          <span className="inline-flex rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-red-200">
            {t('Live')}
          </span>
          <p className="mt-1.5 text-[11px] font-semibold text-white">
            {match.minuteLabel || (match.kickoff ? formatTime(match.kickoff, locale) : '--:--')}
          </p>
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="space-y-2.5">
            <LiveTeamLine
              name={match.homeName}
              logoUrl={match.homeLogo}
              score={match.homeScore}
              pointsBadgeValue={match.virtualPoints}
            />
            <LiveTeamLine
              name={match.awayName}
              logoUrl={match.awayLogo}
              score={match.awayScore}
            />
          </div>

        </div>
      </div>
    </Link>
  )
}

function MatchRowCard({
  match,
  locale,
  onClick,
}: {
  match: DisplayMatch
  locale: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="match-center-match-card block w-full rounded-[1rem] bg-[#0b0f10] px-4 py-3 text-left transition hover:bg-[#151a1c]"
    >
      <div className="flex items-start gap-3">
        <div className="w-14 shrink-0 pt-0.5 text-center text-xs font-bold text-white">
          {match.kickoff ? formatTime(match.kickoff, locale) : '--:--'}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-2.5">
            <TeamLine
              name={match.homeName}
              logoUrl={match.homeLogo}
              score={match.homePred}
            />
            <TeamLine
              name={match.awayName}
              logoUrl={match.awayLogo}
              score={match.awayPred}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {match.homePred == null || match.awayPred == null ? (
              <PredictBadge
                isPredictable={match.canPredict}
                isCancelled={match.isCancelled}
              />
            ) : null}
            {match.scopeText ? <StatusPill label={match.scopeText} tone="muted" /> : null}
          </div>
        </div>
      </div>
    </button>
  )
}

function LiveTeamLine({
  name,
  logoUrl,
  score,
  pointsBadgeValue,
}: {
  name: string
  logoUrl: string | undefined
  score: number | undefined
  pointsBadgeValue?: number | undefined
}) {
  return (
    <div className="flex items-center gap-2">
      <TeamLogo name={name} logoUrl={logoUrl} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-surface-100 sm:text-base">{name}</span>
      {typeof pointsBadgeValue === 'number' ? <PointsBadge points={pointsBadgeValue} /> : null}
      <span className="min-w-[1.5rem] text-right text-base font-black text-white sm:text-lg">
        {score ?? 0}
      </span>
    </div>
  )
}

function PredictionCarousel({
  locale,
  match,
  currentIndex,
  total,
  onBack,
  onPrevious,
  onNext,
  homePred,
  awayPred,
  onPickScore,
  onChangeHome,
  onChangeAway,
  onSave,
  isSaving,
  errorMessage,
}: {
  locale: string
  match: DisplayMatch
  currentIndex: number
  total: number
  onBack: () => void
  onPrevious: () => void
  onNext: () => void
  homePred: number | ''
  awayPred: number | ''
  onPickScore: (home: number, away: number) => void
  onChangeHome: (value: number | '') => void
  onChangeAway: (value: number | '') => void
  onSave: () => void
  isSaving: boolean
  errorMessage?: string | undefined
}) {
  const { t } = useTranslation()
  const touchStartX = useRef<number | null>(null)

  const handleTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null
  }

  const handleTouchEnd = (event: React.TouchEvent<HTMLElement>) => {
    const startX = touchStartX.current
    const endX = event.changedTouches[0]?.clientX
    touchStartX.current = null

    if (startX == null || endX == null || Math.abs(endX - startX) < 45) return
    if (endX < startX) {
      onNext()
    } else {
      onPrevious()
    }
  }

  return (
    <section
      className="match-center-match-card animate-prediction-card-in min-w-0 max-w-full touch-pan-y overflow-hidden rounded-[1rem] border border-[#202427] bg-[#060707] p-3.5 shadow-[0_18px_56px_rgba(0,0,0,0.42)]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSaving}
          className="rounded-[0.75rem] px-2 py-1.5 text-xs font-semibold text-surface-400 transition hover:bg-[#151a1c] hover:text-white disabled:opacity-40"
        >
          ← {t('Terug')}
        </button>
        <div className="text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-300">
            {t('Snel voorspellen')}
          </p>
          <p className="mt-0.5 text-[11px] font-semibold text-surface-500">
            {currentIndex >= 0 ? `${currentIndex + 1} ${t('van')} ${total}` : t('Wedstrijd')}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrevious}
            disabled={isSaving || currentIndex <= 0}
            aria-label={t('Vorige wedstrijd')}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#2a3242] bg-[#151a1c] text-lg text-surface-300 transition hover:border-surface-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={isSaving || currentIndex < 0 || currentIndex >= total - 1}
            aria-label={t('Volgende wedstrijd')}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#2a3242] bg-[#151a1c] text-lg text-surface-300 transition hover:border-surface-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            ›
          </button>
        </div>
      </div>

      <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
              {match.groupLabel ?? t('Wedstrijd')}
            </p>
            <div className="mt-2.5 flex items-center justify-between gap-2.5">
              <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <LargeTeamLogo name={match.homeName} logoUrl={match.homeLogo} />
                <p className="max-w-full truncate text-sm font-medium text-surface-200 sm:text-base">{match.homeName}</p>
              </div>
              <div className="min-w-0 shrink-0 text-center">
                <p className="text-xs font-semibold text-surface-400 sm:text-sm">
                  {match.kickoff ? formatDateTime(match.kickoff, locale) : t('Aftrap onbekend')}
                </p>
                <span className="mt-2 inline-flex rounded-full bg-surface-900 px-2.5 py-1 text-xs font-semibold text-surface-400">
                  {t('Stand')}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <LargeTeamLogo name={match.awayName} logoUrl={match.awayLogo} />
                <p className="max-w-full truncate text-sm font-medium text-surface-200 sm:text-base">{match.awayName}</p>
              </div>
            </div>
      </div>

          <div className="mt-4 rounded-[1rem] bg-[#111416] p-3.5">
            <div className="grid grid-cols-3 gap-2">
              {QUICK_SCORE_COLUMNS.map((column) => (
                <div
                  key={column.key}
                  className={[
                    'flex min-h-[12rem] flex-col gap-2',
                    column.align === 'center' ? 'justify-center' : '',
                  ].join(' ')}
                >
                  {column.scores.map(([home, away]) => {
                    const selected = homePred === home && awayPred === away
                    return (
                      <button
                        key={`${column.key}-${home}-${away}`}
                        type="button"
                        onClick={() => onPickScore(home, away)}
                        disabled={!match.canPredict || isSaving}
                        className={[
                          'rounded-[0.8rem] border px-2.5 py-2 text-sm font-black transition sm:text-base',
                          selected
                            ? 'border-accent-500/70 bg-[#182326] text-white shadow-[0_10px_28px_rgba(0,126,181,0.16)]'
                            : 'border-white/[0.08] bg-[#111416] text-surface-300 hover:border-white/[0.18] hover:bg-[#151a1c] focus-visible:border-accent-400 focus-visible:bg-[#151a1c] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111416]',
                          'disabled:cursor-not-allowed disabled:opacity-45',
                        ].join(' ')}
                      >
                        {home}-{away}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>

            <p className="mt-3.5 text-center text-xs font-semibold text-surface-300 sm:text-sm">
              {t('Vul de uitslag in')}
            </p>

            <div className="mt-3 flex items-center justify-center gap-2.5">
              <ScoreInput
                value={homePred}
                onChange={onChangeHome}
                disabled={!match.canPredict || isSaving}
              />
              <span className="text-base font-black text-surface-400 sm:text-lg">-</span>
              <ScoreInput
                value={awayPred}
                onChange={onChangeAway}
                disabled={!match.canPredict || isSaving}
              />
            </div>
          </div>

          {match.scopeText ? (
            <p className="mt-3 text-center text-[11px] text-surface-400 sm:text-xs">{match.scopeText}</p>
          ) : null}
          {errorMessage ? (
            <p className="mt-3 rounded-lg bg-red-950/50 px-3.5 py-2.5 text-center text-[11px] text-red-200 sm:text-xs">
              {errorMessage}
            </p>
          ) : null}

        <button
            type="button"
            onClick={onSave}
            disabled={
              !match.canPredict ||
              match.representativePouleId == null ||
              homePred === '' ||
              awayPred === '' ||
              isSaving
            }
            className="mt-3.5 flex w-full items-center justify-center rounded-[0.9rem] bg-accent-600 px-4 py-2.5 text-sm font-black text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-45 sm:text-base"
        >
            {isSaving ? t('Opslaan…') : t('Uitslag invullen')}
        </button>
    </section>
  )
}

function ErrorPanel({
  title,
  body,
  cta,
  onClick,
}: {
  title: string
  body: string
  cta: string
  onClick: () => void
}) {
  return (
    <div className="rounded-4xl border border-red-900/60 bg-red-950/40 p-8">
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-300">
        {title}
      </p>
      <p className="mt-3 max-w-2xl text-sm text-red-100/80">{body}</p>
      <button
        type="button"
        onClick={onClick}
        className="mt-6 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-400"
      >
        {cta}
      </button>
    </div>
  )
}

function MetricBlock({ value, label }: { value: string; label: string }) {
  return (
    <div className="px-3">
      <p className="text-lg font-black text-white sm:text-xl">{value}</p>
      <p className="mt-1 text-sm text-surface-500">{label}</p>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-[0.9rem] bg-[#111416] px-3.5 py-2.5">
      <span className="text-xs text-surface-400 sm:text-sm">{label}</span>
      <span className="text-xs font-bold text-white sm:text-sm">{value}</span>
    </div>
  )
}

function TeamLine({
  name,
  logoUrl,
  score,
}: {
  name: string
  logoUrl: string | undefined
  score: number | undefined
}) {
  return (
    <div className="flex items-center gap-2">
      <TeamLogo name={name} logoUrl={logoUrl} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-surface-100 sm:text-base">{name}</span>
      <span className="min-w-[1.5rem] text-right text-base font-black text-white">
        {score != null ? score : '•'}
      </span>
    </div>
  )
}

function TeamLogo({
  name,
  logoUrl,
}: {
  name: string
  logoUrl: string | undefined
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="h-8 w-8 shrink-0 object-contain"
      />
    )
  }

  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-800 text-[10px] font-black uppercase text-surface-300">
      {getInitials(name)}
    </span>
  )
}

function LargeTeamLogo({
  name,
  logoUrl,
}: {
  name: string
  logoUrl: string | undefined
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="h-10 w-14 object-contain sm:h-12 sm:w-16"
      />
    )
  }

  return (
    <span className="flex h-10 w-14 items-center justify-center rounded-sm bg-surface-800 text-sm font-black uppercase text-surface-300 sm:h-12 sm:w-16 sm:text-base">
      {getInitials(name)}
    </span>
  )
}

function PouleLogo({
  label,
  thumbnailUrl,
}: {
  label: string
  thumbnailUrl: string | undefined
}) {
  if (thumbnailUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt=""
        className="h-7 w-7 rounded-md object-cover"
      />
    )
  }

  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-surface-800 text-[10px] font-black uppercase text-surface-300">
      {getInitials(label)}
    </span>
  )
}

function PredictBadge({
  isPredictable,
  isCancelled,
}: {
  isPredictable: boolean
  isCancelled: boolean
}) {
  const { t } = useTranslation()
  if (isCancelled) {
    return <StatusPill label={t('Geannuleerd')} tone="danger" />
  }

  // The match card itself is already the entry point for prediction. Keep the
  // card clear of a duplicate action pill, while retaining closed/cancelled
  // state feedback where it is meaningful.
  if (isPredictable) return null

  return <StatusPill label={t('Gesloten')} tone="muted" />
}

function StatusPill({
  label,
  tone,
}: {
  label: string
  tone: 'action' | 'muted' | 'danger'
}) {
  const className =
    tone === 'action'
      ? 'bg-accent-500/15 text-accent-300'
      : tone === 'danger'
        ? 'border-red-500/30 bg-red-500/10 text-red-200'
        : 'border-surface-700 bg-surface-900 text-surface-400'

  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${className}`}
    >
      {label}
    </span>
  )
}

function ScoreInput({
  value,
  onChange,
  disabled,
}: {
  value: number | ''
  onChange: (value: number | '') => void
  disabled: boolean
}) {
  return (
    <input
      type="number"
      min="0"
      max="99"
      value={value}
      disabled={disabled}
      onChange={(event) => {
        const raw = event.target.value
        onChange(raw === '' ? '' : Number(raw))
      }}
      className="score-input-no-spinner h-9 w-11 rounded-[0.8rem] border border-white/[0.08] bg-[#111416] px-2 text-center text-base font-black text-white outline-none transition hover:border-white/[0.18] focus:border-accent-400 focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111416] disabled:opacity-50 sm:h-10 sm:w-12 sm:text-lg"
    />
  )
}

function getUserLabel(displayName?: string | null, fallback = 'Speler') {
  const normalized = displayName?.trim()
  return normalized && normalized.length > 0 ? normalized : fallback
}

function getDashboardBadges(dashboard: HomeDashboard | undefined): HomeBadge[] {
  return [...(dashboard?.badges_earned_today ?? [])]
}

function getDashboardBadgesCount(dashboard: HomeDashboard | undefined) {
  return dashboard?.badges_earned_today_count ?? dashboard?.badge_count ?? 0
}

function getBadgeName(badge: HomeBadge) {
  return (
    normalizeOptionalString(badge.name) ??
    normalizeOptionalString(badge.badge?.name) ??
    translate('Trofee')
  )
}

function getBadgeTierLabel(badge: HomeBadge) {
  return (
    normalizeOptionalString(badge.tier?.label) ??
    normalizeOptionalString(badge.payloadTierLabel)
  )
}

function getBadgeDifficultyLabel(badge: HomeBadge) {
  const difficulty = badge.difficulty ?? badge.badge?.difficulty
  return typeof difficulty === 'number'
    ? translate('Moeilijkheid {value}', getLocale(), { value: difficulty })
    : undefined
}

function getBadgeVisualUrl(badge: HomeBadge) {
  return (
    normalizeOptionalString(badge.club?.logo_url) ??
    normalizeOptionalString(badge.team?.logo_url) ??
    normalizeOptionalString(badge.icon) ??
    normalizeOptionalString(badge.icon_url) ??
    normalizeOptionalString(badge.badge?.icon_url)
  )
}

function getBadgeContextLabel(badge: HomeBadge) {
  return (
    normalizeOptionalString(badge.club?.display_name ?? badge.club?.name) ??
    normalizeOptionalString(badge.team?.display_name ?? badge.team?.name)
  )
}

function getBadgePouleLabel(badge: HomeBadge) {
  return normalizeOptionalString(badge.poule?.name)
}

function getBadgeRelatedMatches(badge: HomeBadge): HomeBadgeMatch[] {
  const source = badge.matches && badge.matches.length > 0
    ? badge.matches
    : badge.match
      ? [badge.match]
      : []

  return source.map((match) => ({
    id: match.id,
    homeName: getTeamName(match.home_team),
    awayName: getTeamName(match.away_team),
    homeLogo: getTeamLogo(match.home_team),
    awayLogo: getTeamLogo(match.away_team),
    kickoff: getKickoffSource(match.kickoff_utc, match.kickoff_nl),
    homeScore: match.score?.home ?? undefined,
    awayScore: match.score?.away ?? undefined,
    homePred:
      badge.prediction?.home_pred ??
      match.user_prediction?.home_pred ??
      undefined,
    awayPred:
      badge.prediction?.away_pred ??
      match.user_prediction?.away_pred ??
      undefined,
    pointsAwarded:
      badge.prediction?.points_awarded ??
      match.user_prediction?.points_awarded ??
      undefined,
  }))
}

function getMatchCenterState(
  liveSummary: HomeLiveSummary | undefined,
  nextSummary: HomeNextSummary | undefined,
  locale: string,
  t: (key: string, values?: Record<string, string | number>) => string,
) {
  if (getHasLiveMatches(liveSummary)) {
    return {
      icon: '•',
      tone: 'live' as const,
      title: t('{count} live', { count: formatCount(liveSummary?.matches_count) }),
      subtitle: t('Er zijn op dit moment live wedstrijden bezig.'),
      meta: t('Live bezig'),
      info: t('Open de lijst om direct je live en komende wedstrijden te bekijken.'),
      cta: t('Bekijk live wedstrijden'),
      progress: 100,
    }
  }

  const matchesCount = nextSummary?.matches_count ?? 0
  const unpredictedCount = getOpenUnpredictedCount(nextSummary)
  const semanticMatchesCount = getSemanticMatchesCount(nextSummary)
  const progressMatchesCount = semanticMatchesCount || matchesCount

  if (!nextSummary?.has_matches || matchesCount === 0) {
    return {
      icon: '–',
      tone: 'empty' as const,
      title: t('Geen wedstrijden beschikbaar'),
      subtitle: t('Er staan nu geen komende wedstrijden voor je klaar.'),
      meta: t('Geen open acties'),
      info: t('Zodra er weer wedstrijden zijn, zie je hier direct wat je moet invullen.'),
      cta: t('Bekijk mijn wedstrijden'),
      progress: 10,
    }
  }

  if (unpredictedCount > 0) {
    return {
      icon: '•',
      tone: 'action' as const,
      title: t('Nog {count} voorspellen', { count: unpredictedCount }),
      subtitle: t('Van {matches} zichtbare wedstrijden staan er nog {count} open.', {
        matches: matchesCount,
        count: unpredictedCount,
      }),
      meta: `${t('Eerste aftrap')} ${formatKickoffDateTime(
        nextSummary.next_kickoff_utc,
        nextSummary.next_kickoff_local,
        locale,
      )}`,
      info: t('Klik op de blauwe knop en vul direct je wedstrijden in.'),
      cta: t('Voorspel wedstrijden'),
      progress: clampProgress(
        Math.round(
          ((progressMatchesCount - unpredictedCount) / progressMatchesCount) * 100,
        ),
      ),
    }
  }

  return {
    icon: '✓',
    tone: 'ready' as const,
    title: t('Je bent klaar voor nu'),
    subtitle: t('Je eerstvolgende wedstrijden zijn ingevuld.'),
    meta: `${t('Eerste aftrap')} ${formatKickoffDateTime(
      nextSummary.next_kickoff_utc,
      nextSummary.next_kickoff_local,
      locale,
    )}`,
    info: t('Open de lijst om je ingevulde wedstrijden nog eens na te lopen.'),
    cta: t('Bekijk mijn wedstrijden'),
    progress: 100,
  }
}

function getOpenUnpredictedCount(nextSummary: HomeNextSummary | undefined) {
  const matchCenter = nextSummary?.match_center
  if (!matchCenter) return nextSummary?.unpredicted_count ?? 0

  return (
    (matchCenter.regular?.next_matches_open_unpredicted_count ?? 0) +
    (matchCenter.stage_by_stage?.current_stage_open_unpredicted_matches_count ?? 0)
  )
}

function getSemanticMatchesCount(nextSummary: HomeNextSummary | undefined) {
  const matchCenter = nextSummary?.match_center
  if (!matchCenter) return 0

  return (
    (matchCenter.regular?.next_matches_total_count ?? 0) +
    (matchCenter.stage_by_stage?.current_stage_total_matches_count ?? 0)
  )
}

function getDeduplicatedMatches(data: MatchesData | undefined): DisplayMatch[] {
  if (!data?.deduplicated_matches?.visible) return []

  return data.deduplicated_matches.items.map((match) => ({
    id: match.match_id,
    homeName: getTeamName(match.home_team),
    awayName: getTeamName(match.away_team),
    homeLogo: getTeamLogo(match.home_team),
    awayLogo: getTeamLogo(match.away_team),
    kickoff: normalizeOptionalString(match.kickoff),
    homePred: getPredictionField(match, 'home_pred'),
    awayPred: getPredictionField(match, 'away_pred'),
    representativePouleId:
      match.representative_poule_id ??
      match.poule_id ??
      match.user_prediction?.poule_id ??
      match.prediction_scope?.poules[0]?.id,
    isPredictable: match.is_predictable === true,
    canPredict: getCanPredict(
      match.is_predictable,
      match.is_cancelled,
      normalizeOptionalString(match.predict_status),
      match.representative_poule_id ??
        match.poule_id ??
        match.user_prediction?.poule_id ??
        match.prediction_scope?.poules[0]?.id,
    ),
    isCancelled: match.is_cancelled === true,
    statusLabel: getMatchStatusLabel(
      match.is_predictable,
      match.is_cancelled,
      normalizeOptionalString(match.predict_status),
    ),
    scopeText: formatScopeText(match.prediction_scope),
    groupLabel: normalizeOptionalString(match.prediction_scope?.poules[0]?.name),
  }))
}

function getPouleGroups(data: MatchesData | undefined) {
  return (data?.poules ?? [])
    .map((poule) => ({
      id: poule.id,
      label: poule.name,
      thumbnailUrl: normalizeOptionalString(poule.thumbnail_url),
      matches: poule.matches.map((match) => toDisplayMatch(match, poule)),
    }))
    .filter((group) => group.matches.length > 0)
}

function getAllDisplayMatches(data: MatchesData | undefined): DisplayMatch[] {
  return getPouleGroups(data).flatMap((group) => group.matches)
}

function getDisplayMatchKey(match: DisplayMatch): string {
  return `${match.representativePouleId ?? 'poule'}-${match.id}`
}

function getPredictionQueue(data: MatchesData | undefined): DisplayMatch[] {
  return getAllDisplayMatches(data).filter(
    (match) =>
      match.canPredict && (match.homePred == null || match.awayPred == null),
  )
}

function getNextPredictionMatch(
  data: MatchesData | undefined,
  currentMatch: DisplayMatch | null,
): DisplayMatch | null {
  const queue = getPredictionQueue(data)
  if (queue.length === 0) return null

  const currentKey = currentMatch ? getDisplayMatchKey(currentMatch) : null
  return queue.find((match) => getDisplayMatchKey(match) !== currentKey) ?? null
}

function getLivePouleGroups(data: LiveMatchesData | undefined) {
  return (data?.poules ?? [])
    .map((poule) => ({
      id: poule.id,
      label: poule.name,
      thumbnailUrl: normalizeOptionalString(poule.thumbnail_url),
      matches: [...poule.matches]
        .sort((left, right) => {
          const leftValue = getKickoffTimeValue(left.kickoff_utc, left.kickoff_nl)
          const rightValue = getKickoffTimeValue(right.kickoff_utc, right.kickoff_nl)
          return leftValue - rightValue
        })
        .map((match) => toLiveDisplayMatch(match)),
    }))
    .filter((group) => group.matches.length > 0)
}

function getRecentPointGroups(
  recentPoules: HomeRecentMatches['poules'] | undefined,
  dashboardPoules: HomeDashboard['poules'] | undefined,
) {
  const source = recentPoules && recentPoules.length > 0 ? recentPoules : dashboardPoules ?? []

  return source
    .map((poule) => ({
      id: poule.id,
      label: poule.name,
      thumbnailUrl: normalizeOptionalString(poule.thumbnail_url),
      matches: [...poule.matches]
        .sort((left, right) => {
          const leftValue = getKickoffTimeValue(left.kickoff_utc, left.kickoff_nl)
          const rightValue = getKickoffTimeValue(right.kickoff_utc, right.kickoff_nl)
          return rightValue - leftValue
        })
        .map((match) => toRecentDisplayMatch(match)),
    }))
    .filter((group) => group.matches.length > 0)
}

function toDisplayMatch(match: PouleMatch, poule: PouleSection): DisplayMatch {
  return {
    id: match.id,
    homeName: getTeamName(match.home_team),
    awayName: getTeamName(match.away_team),
    homeLogo: getTeamLogo(match.home_team),
    awayLogo: getTeamLogo(match.away_team),
    kickoff: getKickoffSource(match.kickoff_utc, match.kickoff_nl),
    homePred: getPredictionField(match, 'home_pred'),
    awayPred: getPredictionField(match, 'away_pred'),
    representativePouleId: match.poule_id ?? poule.id,
    isPredictable: match.is_predictable === true,
    canPredict: getCanPredict(
      match.is_predictable,
      match.is_cancelled,
      normalizeOptionalString(match.predict_status ?? match.status),
      match.poule_id ?? poule.id,
    ),
    isCancelled: match.is_cancelled === true,
    statusLabel: getMatchStatusLabel(
      match.is_predictable,
      match.is_cancelled,
      normalizeOptionalString(match.predict_status ?? match.status),
    ),
    scopeText: undefined,
    groupLabel: poule.name,
  }
}

function getPredictionField<
  T extends {
    user_prediction?: {
      home_pred?: number | null | undefined
      away_pred?: number | null | undefined
      points_awarded?: number | null | undefined
      virtual_points?: number | null | undefined
      poule_id?: number | undefined
    } | null | undefined
  },
>(
  match: T,
  key: 'home_pred' | 'away_pred' | 'points_awarded' | 'virtual_points',
) {
  const nestedValue = match.user_prediction?.[key]
  if (typeof nestedValue === 'number') return nestedValue

  const inlineValue = (match as Record<string, unknown>)[key]
  return typeof inlineValue === 'number' ? inlineValue : undefined
}

function toRecentDisplayMatch(match: RecentMatch | HomeDashboardMatch): RecentDisplayMatch {
  return {
    id: match.id,
    homeName: getTeamName(match.home_team),
    awayName: getTeamName(match.away_team),
    homeLogo: getTeamLogo(match.home_team),
    awayLogo: getTeamLogo(match.away_team),
    kickoff: getKickoffSource(match.kickoff_utc, match.kickoff_nl),
    homeScore: match.score?.home ?? undefined,
    awayScore: match.score?.away ?? undefined,
    homePred: match.user_prediction?.home_pred ?? undefined,
    awayPred: match.user_prediction?.away_pred ?? undefined,
    pointsAwarded: match.user_prediction?.points_awarded ?? 0,
  }
}

function toLiveDisplayMatch(match: LivePouleMatch): LiveDisplayMatch {
  return {
    id: match.id,
    representativePouleId: match.poule_id,
    pouleMatchId: match.poule_match_id,
    homeName: getTeamName(match.home_team),
    awayName: getTeamName(match.away_team),
    homeLogo: getTeamLogo(match.home_team),
    awayLogo: getTeamLogo(match.away_team),
    kickoff: getKickoffSource(match.kickoff_utc, match.kickoff_nl),
    minuteLabel: getLiveMinuteLabel(match.minute, match.extra_minute),
    statusLabel: getLiveStatusLabel(match.status),
    homeScore: match.score?.home ?? undefined,
    awayScore: match.score?.away ?? undefined,
    virtualPoints:
      match.user_prediction?.virtual_points
      ?? match.virtual_points
      ?? undefined,
  }
}

function getLiveMatchDetailPath(match: Pick<LiveDisplayMatch, 'id' | 'representativePouleId' | 'pouleMatchId'>) {
  if (typeof match.representativePouleId === 'number' && typeof match.pouleMatchId === 'number') {
    const params = new URLSearchParams({
      pouleMatchId: String(match.pouleMatchId),
      sourceTab: 'dashboard',
    })
    return `/poules/${match.representativePouleId}/wedstrijden/${match.id}?${params.toString()}`
  }

  return `/wedstrijden/${match.id}`
}

function getMultiPouleBannerText(data: MatchesData | undefined) {
  if (!data?.deduplicated_matches?.visible) return undefined

  const maxPouleCount = data.deduplicated_matches.items.reduce((max, item) => {
    const count = item.prediction_scope?.poule_count ?? 0
    return item.prediction_scope?.applies_to_all ? Math.max(max, count) : max
  }, 0)

  if (maxPouleCount <= 1) return undefined
  return translate('Je doet mee aan meerdere poules.')
}

function getMatchStatusLabel(
  isPredictable?: boolean,
  isCancelled?: boolean,
  predictStatus?: string | null,
) {
  if (isCancelled) return translate('Geannuleerd')
  if (isPredictable) return translate('Open')
  if (predictStatus === 'closed') return translate('Gesloten')
  if (predictStatus === 'open') return translate('Open')
  if (predictStatus === 'mixed') return translate('Gedeeltelijk open')
  return translate('Niet beschikbaar')
}

function getLiveStatusLabel(status?: string | null) {
  if (!status) return translate('Live')
  if (status === 'interrupted') return translate('Onderbroken')
  if (status === 'live') return translate('Live')
  return status
}

function getLiveMinuteLabel(
  minute?: number | null,
  extraMinute?: number | null,
) {
  if (typeof minute !== 'number') return ''
  if (typeof extraMinute === 'number' && extraMinute > 0) {
    return `${minute}+${extraMinute}'`
  }

  return `${minute}'`
}

function getHasLiveMatches(
  summary?: HomeLiveSummary | { has_live_matches?: boolean | undefined; matches_count?: number | undefined },
) {
  if (!summary) return false
  if (typeof summary.has_live_matches === 'boolean') {
    return summary.has_live_matches
  }

  return (summary.matches_count ?? 0) > 0
}

function getCanPredict(
  isPredictable?: boolean,
  isCancelled?: boolean,
  predictStatus?: string | null,
  representativePouleId?: number,
) {
  if (isCancelled) return false
  if (representativePouleId == null) return false
  if (isPredictable === true) return true

  return predictStatus === 'open' || predictStatus === 'mixed'
}

function getTeamName(team?: {
  display_name?: string | null | undefined
  short_name?: string | null | undefined
  name?: string | null | undefined
} | null) {
  return team?.display_name ?? team?.short_name ?? team?.name ?? translate('Onbekend team')
}

function getTeamLogo(team?: { logo_url?: string | null | undefined } | null) {
  return normalizeOptionalString(team?.logo_url)
}

function formatScopeText(scope?: {
  applies_to_all?: boolean | undefined
  poule_count?: number | undefined
  poules?: Array<{ name?: string | null | undefined }> | undefined
} | null) {
  if (!scope?.applies_to_all) return undefined

  const count = scope.poule_count ?? scope.poules?.length ?? 0
  return translate('Je doet mee aan {count} poules', getLocale(), { count })
}

function clampProgress(value: number) {
  return Math.max(8, Math.min(100, value))
}

function getMatchTimeValue(value?: string | null) {
  if (!value) return Number.MAX_SAFE_INTEGER

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return Number.MAX_SAFE_INTEGER
  return date.getTime()
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function formatCount(value?: number) {
  return typeof value === 'number' ? String(value) : '0'
}

function formatPoints(value?: number) {
  return typeof value === 'number' ? String(value) : '0'
}

function formatSignedPoints(value?: number) {
  const formatted = typeof value === 'number' ? `${value > 0 ? '+' : ''}${value}` : '0'
  return translate('{value} pt', getLocale(), { value: formatted })
}

function formatDate(value: string, locale: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date)
}

function formatTime(value: string, locale: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '--:--'

  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function formatDateTime(value: string, locale: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function normalizeOptionalString(value?: string | null) {
  return value ?? undefined
}

function MyPoulesTab({
  isLoading,
  isError,
  items,
}: {
  isLoading: boolean
  isError: boolean
  items: JoinedPoule[]
}) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="space-y-3" aria-label={t('Mijn poules laden')}>
        {Array.from({ length: 3 }, (_, index) => (
          <div
            key={index}
            className="h-16 animate-pulse rounded-[1rem] bg-[#111416]"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-[1rem] border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-200">
        {t('Mijn poules konden niet worden geladen.')}
      </div>
    )
  }

  const createPouleAction = (
    <Link
      to="/dashboard/poules/aanmaken"
      className="inline-flex min-h-10 items-center justify-center rounded-lg bg-accent-600 px-3.5 py-2 text-sm font-bold text-white transition hover:bg-accent-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-950"
    >
      <span className="mr-2 text-lg leading-none" aria-hidden="true">+</span>
      {t('Poule maken')}
    </Link>
  )

  if (items.length === 0) {
    return (
      <div className="rounded-[1rem] bg-[#111416] p-4 text-sm text-surface-400">
        <p>{t('Je hebt nog geen poules.')}</p>
        <div className="mt-4">{createPouleAction}</div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">{createPouleAction}</div>
      <div className="space-y-2.5">
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/poules/${item.id}`}
            className="my-poule-card group flex items-center gap-3 rounded-[1rem] bg-[#111416] px-3.5 py-3 transition hover:bg-[#151a1c]"
          >
            <PouleThumb name={item.name} thumbnailUrl={normalizeString(item.thumbnail_url)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-3">
                <div className="min-w-0 pl-3">
                  <h2 className="truncate text-sm font-black text-white">{item.name}</h2>
                  {normalizeString(item.description) ? (
                    <p className="mt-1 truncate text-xs text-surface-400">{normalizeString(item.description)}</p>
                  ) : null}
                  <p className="mt-1 text-xs font-semibold text-surface-500">
                    {t('{count} leden', { count: formatPoulesNumber(item.members_count) })}
                  </p>
                </div>
                <span className="ml-auto shrink-0 text-lg text-surface-500">›</span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <MiniStat
                  label={t('Punten')}
                  value={formatPoulesNumber(item.my_points)}
                  plain
                  className="border-l border-white/[0.08]"
                />
                <MiniStat
                  label={t('Positie')}
                  value={item.my_position ? `#${item.my_position}` : '–'}
                  plain
                  className="border-l border-white/[0.08]"
                />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

function DashboardPageSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="space-y-6" aria-label={t('Home laden')}>
      <div className="space-y-2">
        <div className="h-8 w-48 animate-pulse rounded-lg bg-[#0d1113]" />
        <div className="h-5 w-72 max-w-full animate-pulse rounded-lg bg-[#0d1113]" />
      </div>
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <div className="h-[34rem] animate-pulse rounded-[1.75rem] bg-[#0d1113]" />
        <div className="space-y-6">
          <div className="h-56 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
          <div className="h-56 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
        </div>
      </div>
    </div>
  )
}

function MyStatsTab({
  locale,
  isLoading,
  isError,
  data,
}: {
  locale: string
  isLoading: boolean
  isError: boolean
  data: MyStatsDashboard | undefined
}) {
  const { t } = useTranslation()
  if (isLoading) {
    return (
      <div className="rounded-[1rem] bg-[#111416] p-4 text-sm text-surface-400">
        {t('Mijn stats laden…')}
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="rounded-[1rem] border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-200">
        {t('Mijn stats konden niet worden geladen.')}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard label={t('Totaal punten')} value={formatPoulesNumber(data.total_points)} />
        <StatCard label={t('Gem. punten')} value={formatAverage(data.avg_points)} />
        <StatCard label={t('Gem. vorm')} value={formatAverage(data.avg_form)} />
        <StatCard label={t('Voorspellingen')} value={formatPoulesNumber(data.total_predictions)} />
        <StatCard
          label={t('Met punten')}
          value={formatPoulesNumber(data.predictions_with_points)}
        />
        <StatCard
          label={t('Laatste voorspelling')}
          value={formatShortDateTime(data.last_prediction_at, locale)}
        />
      </div>

      <section className="rounded-[1rem] bg-[#111416] p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-black text-white">
            {normalizeString(data.skill?.summary?.title) || t('Skill')}
          </h2>
          {normalizeString(data.skill?.rank?.name) ? (
            <span className="rounded-full bg-accent-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent-300">
              {normalizeString(data.skill?.rank?.name)}
            </span>
          ) : null}
        </div>
        <p className="mt-2 text-sm text-surface-400">
          {normalizeString(data.skill?.summary?.subtitle) || t('Nog geen extra uitleg beschikbaar.')}
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <MiniStat
            label={t('Rank score')}
            value={formatPoulesNumber(data.skill?.progress?.current_rank_score)}
          />
          <MiniStat
            label={t('Performance')}
            value={normalizeString(data.performance?.label) || t('Onbekend')}
          />
          <MiniStat
            label={t('Volgende rank')}
            value={
              data.skill?.progress?.is_max_rank
                ? t('Max')
                : normalizeString(data.skill?.progress?.next_rank?.name) || t('Onbekend')
            }
          />
          <MiniStat
            label={t('Nodig voor next')}
            value={
              data.skill?.progress?.is_max_rank
                ? '0'
                : formatPoulesNumber(data.skill?.progress?.points_to_next_rank)
            }
          />
        </div>
      </section>

      {data.skill?.highlights?.length ? (
        <section className="rounded-[1rem] bg-[#111416] p-4">
          <h2 className="text-sm font-black uppercase tracking-[0.18em] text-surface-500">
            {t('Highlights')}
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {data.skill.highlights.map((highlight, index) => (
              <div
                key={`${highlight.key ?? highlight.label ?? 'highlight'}-${index}`}
                className="rounded-[0.95rem] bg-[#111416] px-4 py-3.5"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
                  {normalizeString(highlight.label) || t('Highlight')}
                </p>
                <p className="mt-2 text-lg font-black text-white">
                  {normalizeString(highlight.value) || '–'}
                </p>
                <p className="mt-1 text-xs text-surface-400">
                  {normalizeString(highlight.hint) || t('Geen toelichting')}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function PouleThumb({
  name,
  thumbnailUrl,
}: {
  name: string
  thumbnailUrl: string | undefined
}) {
  if (thumbnailUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt=""
        className="h-11 w-11 shrink-0 rounded-[0.85rem] object-cover"
      />
    )
  }

  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.85rem] bg-surface-800 text-sm font-black uppercase text-surface-300">
      {getInitials(name)}
    </span>
  )
}

function MiniStat({
  label,
  value,
  hoverClassName,
  className,
  plain = false,
}: {
  label: string
  value: string
  hoverClassName?: string
  className?: string
  plain?: boolean
}) {
  return (
    <div
      className={[
        plain ? 'px-3 py-2.5' : 'rounded-[0.9rem] bg-[#111416] px-3 py-2.5',
        hoverClassName,
        className,
      ].filter(Boolean).join(' ')}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-bold text-white">{value}</p>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[0.95rem] bg-[#111416] px-4 py-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
        {label}
      </p>
      <p className="mt-2 text-lg font-black text-white">{value}</p>
    </div>
  )
}

function formatPoulesNumber(value?: number | null) {
  return typeof value === 'number' ? String(value) : '0'
}

function formatAverage(value?: number | null) {
  return typeof value === 'number' ? value.toFixed(2) : '0.00'
}

function normalizeString(value?: string | null) {
  const normalized = value?.trim()
  return normalized && normalized.length > 0 ? normalized : undefined
}

function formatShortDateTime(value?: string | null, locale?: string) {
  if (!value) return translate('Onbekend')

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(toIntlLocale(locale ?? getLocale()), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}
